import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Trash2, UserCircle, Camera, X, Plus, Upload, Calendar as CalendarIcon, Package, Users as UsersIcon, Receipt, CheckCircle2, Circle, Clock, FileText as FileTextIcon, AlertTriangle, FileSignature } from 'lucide-react';
import PrevNextNav from '@/components/PrevNextNav';
import { usePrevNextNav } from '@/hooks/usePrevNextNav';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ChantierForm from '@/components/forms/ChantierForm';
import ChantierBusinessPanels from '@/components/ChantierBusinessPanels';
import EntityTasksPanel from '@/components/EntityTasksPanel';
import { MissionDialog, MissionsList } from '@/components/MissionDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function ChantierDetail({ chantierId: propChantierId, embedded = false }) {
  const params = useParams();
  const id = propChantierId || params.id;
  const navigate = useNavigate();
  const nav = usePrevNextNav({
    storageKey: 'chantiers_list_order',
    currentId: id,
    routePrefix: '/chantiers',
  });
  const [chantier, setChantier] = useState(null);
  const [contact, setContact] = useState(null);
  const [acomptes, setAcomptes] = useState([]);
  const [artisans, setArtisans] = useState([]);
  const [materiel, setMateriel] = useState([]);
  const [depenses, setDepenses] = useState({ items: [], total_ttc: 0, total_ht: 0 });
  const [factures, setFactures] = useState([]);
  const [showMaterielForm, setShowMaterielForm] = useState(false);
  const [newMateriel, setNewMateriel] = useState({ fournisseur: '', description: '', prix_unitaire: '', quantite: '1', notes: '' });
  const [showLinkFAModal, setShowLinkFAModal] = useState(false);
  const [availableFA, setAvailableFA] = useState([]);
  const [loadingFA, setLoadingFA] = useState(false);
  const [rapports, setRapports] = useState([]);
  const [newRapport, setNewRapport] = useState({ date: new Date().toISOString().slice(0, 10), auteur: '', contenu: '', meteo: '', incidents: '' });
  const [showRapportForm, setShowRapportForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showAcompte, setShowAcompte] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [missionDialogArtisan, setMissionDialogArtisan] = useState(null);
  const [missionsRefreshKey, setMissionsRefreshKey] = useState(0);
  const photoInputRefs = {
    avant: useRef(null),
    pendant: useRef(null),
    apres: useRef(null)
  };
  const [newAcompte, setNewAcompte] = useState({
    montant: '',
    pourcentage: '',
    statut: 'Attendu',
    notes: ''
  });

  useEffect(() => {
    fetchChantier();
    fetchAcomptes();
    fetchArtisans();
    fetchMateriel();
    fetchDepenses();
    fetchFactures();
    fetchRapports();
  }, [id]);

  const fetchRapports = async () => {
    try {
      const r = await api.get(`/chantiers/${id}/rapports`);
      setRapports(r.data.items || []);
    } catch (e) { /* ignore */ }
  };

  const handleAddRapport = async () => {
    if (!newRapport.auteur || !newRapport.contenu) {
      toast.error('Auteur et contenu obligatoires');
      return;
    }
    try {
      await api.post(`/chantiers/${id}/rapports`, newRapport);
      toast.success('Rapport ajouté');
      setNewRapport({ date: new Date().toISOString().slice(0, 10), auteur: newRapport.auteur, contenu: '', meteo: '', incidents: '' });
      setShowRapportForm(false);
      fetchRapports();
    } catch (e) {
      toast.error('Erreur ajout rapport');
    }
  };

  const handleDeleteRapport = async (rapId) => {
    if (!window.confirm('Supprimer ce rapport ?')) return;
    try {
      await api.delete(`/chantiers/${id}/rapports/${rapId}`);
      toast.success('Rapport supprimé');
      fetchRapports();
    } catch (e) { toast.error('Erreur'); }
  };

  const fetchFactures = async () => {
    try {
      // Try to get factures linked to this chantier's devis (if any)
      const chRes = await api.get(`/chantiers/${id}`);
      const devisId = chRes.data.devis_id;
      if (devisId) {
        const facRes = await api.get('/factures');
        const linked = (facRes.data || []).filter(f => f.devis_id === devisId);
        setFactures(linked);
      }
    } catch (e) { /* ignore */ }
  };

  const fetchMateriel = async () => {
    try {
      const response = await api.get(`/chantiers/${id}/materiel`);
      setMateriel(response.data.items || []);
    } catch (error) {
      console.error('Erreur chargement matériel:', error);
    }
  };

  const fetchDepenses = async () => {
    try {
      const response = await api.get(`/chantiers/${id}/depenses`);
      setDepenses(response.data);
    } catch (error) {
      console.error('Erreur chargement dépenses:', error);
    }
  };

  const handleAddMateriel = async () => {
    if (!newMateriel.description) {
      toast.error('Description obligatoire');
      return;
    }
    try {
      await api.post(`/chantiers/${id}/materiel`, {
        fournisseur: newMateriel.fournisseur,
        description: newMateriel.description,
        prix_unitaire: parseFloat(newMateriel.prix_unitaire) || 0,
        quantite: parseFloat(newMateriel.quantite) || 1,
        notes: newMateriel.notes,
      });
      toast.success('Matériel ajouté');
      setShowMaterielForm(false);
      setNewMateriel({ fournisseur: '', description: '', prix_unitaire: '', quantite: '1', notes: '' });
      fetchMateriel();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur ajout matériel');
    }
  };

  const handleDeleteMateriel = async (itemId) => {
    if (!window.confirm('Supprimer cette ligne matériel ?')) return;
    try {
      await api.delete(`/chantiers/${id}/materiel/${itemId}`);
      toast.success('Supprimé');
      fetchMateriel();
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  const openLinkFAModal = async () => {
    setShowLinkFAModal(true);
    setLoadingFA(true);
    try {
      const response = await api.get(`/chantiers/${id}/factures-achat-disponibles`);
      setAvailableFA(response.data.items || []);
    } catch (error) {
      toast.error('Erreur chargement factures disponibles');
    } finally {
      setLoadingFA(false);
    }
  };

  const handleLinkFA = async (faId) => {
    try {
      await api.patch(`/factures-achat/${faId}/chantier`, { chantier_id: id });
      toast.success("Facture d'achat liée au chantier");
      setShowLinkFAModal(false);
      fetchDepenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur liaison');
    }
  };

  const handleUnlinkFA = async (faId) => {
    if (!window.confirm('Délier cette facture d\'achat du chantier ?')) return;
    try {
      await api.patch(`/factures-achat/${faId}/chantier`, { chantier_id: null });
      toast.success('Facture déliée');
      fetchDepenses();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const fetchArtisans = async () => {
    try {
      const response = await api.get(`/chantiers/${id}/artisans`);
      setArtisans(response.data);
    } catch (error) {
      console.error('Erreur chargement artisans:', error);
    }
  };

  const handleRemoveArtisan = async (affectationId) => {
    if (!window.confirm('Voulez-vous vraiment retirer cet artisan du chantier ?')) return;
    try {
      await api.delete(`/affectations/${affectationId}`);
      toast.success('Artisan retiré du chantier');
      fetchArtisans();
    } catch (error) {
      console.error('Erreur désaffectation:', error);
      toast.error('Erreur lors de la désaffectation');
    }
  };

  // Calcul automatique montant <-> pourcentage
  const handleMontantChange = (value) => {
    const montant = parseFloat(value) || 0;
    const montantTotal = chantier?.montant_total_devis || 0;
    const pourcentage = montantTotal > 0 ? ((montant / montantTotal) * 100).toFixed(1) : '';
    setNewAcompte({ ...newAcompte, montant: value, pourcentage: pourcentage });
  };

  const handlePourcentageChange = (value) => {
    const pourcentage = parseFloat(value) || 0;
    const montantTotal = chantier?.montant_total_devis || 0;
    const montant = montantTotal > 0 ? ((pourcentage / 100) * montantTotal).toFixed(2) : '';
    setNewAcompte({ ...newAcompte, pourcentage: value, montant: montant });
  };

  const handleDeleteAcompte = async (acompteId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cet acompte ?')) return;
    try {
      await api.delete(`/acomptes/${acompteId}`);
      toast.success('Acompte supprimé');
      fetchAcomptes();
      fetchChantier();
    } catch (error) {
      console.error('Erreur suppression acompte:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handlePhotoUpload = async (e, photoType) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPhoto(photoType);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/upload/photo/${id}/${photoType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploadée');
      fetchChantier();
    } catch (error) {
      console.error('Erreur upload photo:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleDeletePhoto = async (photoType, photoUrl) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette photo ?')) return;
    try {
      await api.delete(`/photos/${id}/${photoType}?photo_url=${encodeURIComponent(photoUrl)}`);
      toast.success('Photo supprimée');
      fetchChantier();
    } catch (error) {
      console.error('Erreur suppression photo:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAddAcompte = async () => {
    if (!newAcompte.montant || parseFloat(newAcompte.montant) <= 0) {
      toast.error('Veuillez saisir un montant valide');
      return;
    }
    try {
      await api.post('/acomptes', {
        chantier_id: id,
        montant: parseFloat(newAcompte.montant),
        pourcentage: newAcompte.pourcentage ? parseFloat(newAcompte.pourcentage) : null,
        statut: newAcompte.statut,
        notes: newAcompte.notes || null,
        date_encaissement: newAcompte.statut === 'Encaissé' ? new Date().toISOString() : null
      });
      toast.success('Acompte ajouté');
      setShowAcompte(false);
      setNewAcompte({ montant: '', pourcentage: '', statut: 'Attendu', notes: '' });
      fetchAcomptes();
      fetchChantier();
    } catch (error) {
      console.error('Erreur ajout acompte:', error);
      toast.error('Erreur lors de l\'ajout de l\'acompte');
    }
  };

  const fetchChantier = async () => {
    try {
      const response = await api.get(`/chantiers/${id}`);
      setChantier(response.data);
      document.title = `Chantier ${response.data.ville} - Reno Resolve`;
      
      // Fetch contact info
      if (response.data.contact_id) {
        try {
          const contactRes = await api.get(`/contacts/${response.data.contact_id}`);
          setContact(contactRes.data);
        } catch (err) {
          console.error('Erreur chargement contact:', err);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement chantier:', error);
      toast.error('Chantier non trouvé');
      setLoading(false);
      setTimeout(() => navigate('/chantiers'), 2000);
    }
  };

  const fetchAcomptes = async () => {
    try {
      const response = await api.get(`/acomptes/chantier/${id}`);
      setAcomptes(response.data);
    } catch (error) {
      console.error('Erreur chargement acomptes:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce chantier ?')) return;
    
    try {
      await api.delete(`/chantiers/${id}`);
      toast.success('Chantier supprimé');
      navigate('/chantiers');
    } catch (error) {
      console.error('Erreur suppression chantier:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleChantierUpdated = () => {
    setShowEdit(false);
    fetchChantier();
    fetchAcomptes();
    toast.success('Chantier mis à jour');
  };

  const isDateLimitePassed = () => {
    if (!chantier?.date_limite) return false;
    return new Date(chantier.date_limite) < new Date() && chantier.statut !== 'Terminé';
  };

  const totalAcomptesEncaisses = acomptes
    .filter(a => a.statut === 'Encaissé')
    .reduce((sum, a) => sum + a.montant, 0);

  const soldeRestant = (chantier?.montant_total_devis || 0) - totalAcomptesEncaisses;

  const getStatutBadgeVariant = (statut) => {
    const variants = {
      'Prévu': 'secondary',
      'En cours': 'default',
      'Terminé': 'default',
      'Bloqué': 'destructive',
    };
    return variants[statut] || 'default';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header (hidden in embedded mode) */}
      {!embedded && (
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
          <Button variant="ghost" size="sm" onClick={() => navigate('/chantiers')} data-testid="back-btn" className="shrink-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg md:text-3xl font-bold text-slate-900 truncate leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }} title={chantier.nom_interne || chantier.project_title || chantier.ville}>
              {chantier.nom_interne || chantier.project_title || `Chantier - ${chantier.ville || 'sans ville'}`}
            </h1>
            <p className="text-slate-600 font-mono text-[10px] md:text-sm truncate">
              {chantier.chantier_id}{chantier.ville && chantier.ville !== chantier.nom_interne ? ` · ${chantier.ville}` : ''}
            </p>
          </div>
        </div>
        <div className="flex gap-1 md:gap-2 shrink-0 items-center">
          {!embedded && <PrevNextNav {...nav} className="mr-1 hidden md:flex" />}
          <Button variant="outline" size="sm" onClick={() => setShowEdit(true)} data-testid="edit-chantier-btn">
            <Edit2 className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Modifier</span>
          </Button>
          <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="delete-chantier-btn">
            <Trash2 className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Supprimer</span>
          </Button>
        </div>
      </div>
      )}

      {/* Embedded mode: minimal toolbar (Modifier only) */}
      {embedded && (
        <div className="flex items-center justify-end">
          <Button variant="outline" size="sm" onClick={() => setShowEdit(true)} data-testid="edit-chantier-btn-inline">
            <Edit2 className="w-3.5 h-3.5 mr-1" />
            Modifier la fiche chantier
          </Button>
        </div>
      )}

      {/* ---------- PILOTAGE (4 blocs synthétiques) ---------- */}
      <PilotageBloc
        chantier={chantier}
        materiel={materiel}
        depenses={depenses}
        artisans={artisans}
        factures={factures}
        onGoDevis={() => chantier.devis_id && navigate(`/devis/${chantier.devis_id}`)}
        onUpdateMaterielStatut={async (itemId, newStatut) => {
          try {
            await api.patch(`/chantiers/${id}/materiel/${itemId}/statut`, { statut: newStatut });
            toast.success('Statut matériel mis à jour');
            fetchMateriel();
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur');
          }
        }}
      />

      {/* Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200">
            <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-3 md:mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Informations Générales
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Statut</p>
                <Badge className="mt-1" variant={getStatutBadgeVariant(chantier.statut)}>{chantier.statut}</Badge>
              </div>
              <div>
                <p className="text-sm text-slate-500">Ville</p>
                <p className="text-slate-900 font-medium mt-1">{chantier.ville}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-slate-500">Adresse</p>
                <p className="text-slate-900 mt-1">{chantier.adresse_chantier}</p>
              </div>
              {chantier.date_debut && (
                <div>
                  <p className="text-sm text-slate-500">Date début</p>
                  <p className="text-slate-900 mt-1">
                    {format(new Date(chantier.date_debut), 'dd/MM/yyyy', { locale: fr })}
                  </p>
                </div>
              )}
              {chantier.date_fin_estimee && (
                <div>
                  <p className="text-sm text-slate-500">Date fin estimée</p>
                  <p className="text-slate-900 mt-1">
                    {format(new Date(chantier.date_fin_estimee), 'dd/MM/yyyy', { locale: fr })}
                  </p>
                </div>
              )}
              {chantier.date_limite && (
                <div className={isDateLimitePassed() ? 'col-span-2' : ''}>
                  <p className="text-sm text-slate-500">Date Limite ⚠️</p>
                  <p className={`mt-1 font-semibold ${isDateLimitePassed() ? 'text-red-600 text-lg' : 'text-slate-900'}`}>
                    {format(new Date(chantier.date_limite), 'dd/MM/yyyy', { locale: fr })}
                    {isDateLimitePassed() && ' - DÉPASSÉE'}
                  </p>
                </div>
              )}
              {chantier.responsable_interne && (
                <div>
                  <p className="text-sm text-slate-500">Responsable Interne</p>
                  <p className="text-slate-900 mt-1">{chantier.responsable_interne}</p>
                </div>
              )}
            </div>
          </div>

          {/* Section Financière */}
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200">
            <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-3 md:mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Financier
            </h2>
            <div className="grid grid-cols-3 gap-4">
              {chantier.montant_total_devis && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 font-medium">Montant Total Devis</p>
                  <p className="text-2xl font-bold text-blue-700 mt-1">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(chantier.montant_total_devis)}
                  </p>
                </div>
              )}
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-600 font-medium">Acomptes Encaissés</p>
                <p className="text-2xl font-bold text-green-700 mt-1">
                  {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalAcomptesEncaisses)}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {chantier.montant_total_devis ? `${((totalAcomptesEncaisses / chantier.montant_total_devis) * 100).toFixed(1)}%` : '0%'}
                </p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg">
                <p className="text-sm text-amber-600 font-medium">Solde Restant</p>
                <p className="text-2xl font-bold text-amber-700 mt-1">
                  {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(soldeRestant)}
                </p>
              </div>
            </div>

            {/* Liste des acomptes */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Acomptes ({acomptes.length})</h3>
                <Button size="sm" onClick={() => setShowAcompte(true)}>
                  + Ajouter Acompte
                </Button>
              </div>
              {acomptes.length > 0 ? (
                <div className="space-y-2">
                  {acomptes.map((acompte) => (
                    <div
                      key={acompte.acompte_id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900">
                            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(acompte.montant)}
                          </span>
                          {acompte.pourcentage && (
                            <span className="text-sm text-slate-500">({acompte.pourcentage}%)</span>
                          )}
                        </div>
                        {acompte.date_encaissement && (
                          <p className="text-xs text-slate-500">
                            Encaissé le {format(new Date(acompte.date_encaissement), 'dd/MM/yyyy', { locale: fr })}
                          </p>
                        )}
                        {acompte.notes && (
                          <p className="text-xs text-slate-400 mt-1">{acompte.notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={acompte.statut === 'Encaissé' ? 'default' : 'secondary'}>
                          {acompte.statut}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteAcompte(acompte.acompte_id)}
                          data-testid={`delete-acompte-${acompte.acompte_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">Aucun acompte enregistré</p>
              )}
            </div>
          </div>

          {/* Section Budget */}
          {(chantier.budget_prevu || chantier.budget_reel) && (
            <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200">
              <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-3 md:mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Budget Prévisionnel vs Réel
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {chantier.budget_prevu && (
                  <div>
                    <p className="text-sm text-slate-500">Budget Prévu</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(chantier.budget_prevu)}
                    </p>
                  </div>
                )}
                {chantier.budget_reel && (
                  <div>
                    <p className="text-sm text-slate-500">Budget Réel</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(chantier.budget_reel)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section Matériel Prévu + Marges */}
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200" data-testid="materiel-marges-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base md:text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Matériel prévu & Marges
              </h2>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowMaterielForm(!showMaterielForm)}
                data-testid="toggle-add-materiel"
              >
                <Plus className="w-4 h-4 mr-1" />
                {showMaterielForm ? 'Annuler' : 'Ajouter matériel'}
              </Button>
            </div>

            {/* Bandeau récap marges */}
            {(() => {
              const totalMateriel = materiel.reduce((s, it) => s + (parseFloat(it.prix_unitaire) || 0) * (parseFloat(it.quantite) || 1), 0);
              const totalReel = depenses.total_ttc || 0;
              const ca = chantier.montant_total_devis || chantier.budget_prevu || 0;
              const margePrev = ca - totalMateriel;
              const margeReelle = ca - totalReel;
              const fmt = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <p className="text-xs text-slate-500">CA (devis)</p>
                    <p className="text-lg font-bold text-slate-900" data-testid="stat-ca">{fmt(ca)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-xs text-amber-700">Matériel prévu (estim.)</p>
                    <p className="text-lg font-bold text-amber-900" data-testid="stat-materiel-prevu">{fmt(totalMateriel)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-rose-50 border border-rose-200">
                    <p className="text-xs text-rose-700">Dépenses réelles (fact. achat)</p>
                    <p className="text-lg font-bold text-rose-900" data-testid="stat-depenses-reelles">{fmt(totalReel)}</p>
                  </div>
                  <div className={`p-3 rounded-lg border ${margeReelle >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                    <p className={`text-xs ${margeReelle >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>Marge (réelle / prév.)</p>
                    <p className={`text-lg font-bold ${margeReelle >= 0 ? 'text-emerald-900' : 'text-red-900'}`} data-testid="stat-marge">
                      {fmt(margeReelle)} <span className="text-xs font-normal">/ {fmt(margePrev)}</span>
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Formulaire ajout */}
            {showMaterielForm && (
              <div className="mb-5 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Fournisseur</Label>
                    <Input
                      value={newMateriel.fournisseur}
                      onChange={(e) => setNewMateriel({ ...newMateriel, fournisseur: e.target.value })}
                      placeholder="Ex : Point P, Leroy Merlin"
                      data-testid="materiel-fournisseur"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Description *</Label>
                    <Input
                      value={newMateriel.description}
                      onChange={(e) => setNewMateriel({ ...newMateriel, description: e.target.value })}
                      placeholder="Ex : Carrelage 60x60 grès cérame"
                      data-testid="materiel-description"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Prix unitaire (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newMateriel.prix_unitaire}
                      onChange={(e) => setNewMateriel({ ...newMateriel, prix_unitaire: e.target.value })}
                      data-testid="materiel-prix"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Quantité</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newMateriel.quantite}
                      onChange={(e) => setNewMateriel({ ...newMateriel, quantite: e.target.value })}
                      data-testid="materiel-quantite"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Input
                    value={newMateriel.notes}
                    onChange={(e) => setNewMateriel({ ...newMateriel, notes: e.target.value })}
                    placeholder="Optionnel"
                  />
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={handleAddMateriel} data-testid="materiel-save">Enregistrer</Button>
                </div>
              </div>
            )}

            {/* Liste matériel prévu */}
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Matériel prévu ({materiel.length})</p>
              {materiel.length === 0 ? (
                <p className="text-sm text-slate-500 py-3">Aucun matériel prévu pour l&apos;instant.</p>
              ) : (
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                  {materiel.map((item) => {
                    const total = (parseFloat(item.prix_unitaire) || 0) * (parseFloat(item.quantite) || 1);
                    const statut = item.statut || 'À chiffrer';
                    return (
                      <div key={item.item_id} className="flex items-center justify-between p-3 hover:bg-slate-50">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="text-sm font-medium text-slate-900 truncate">{item.description}</p>
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${MAT_STATUS_COLORS[statut]}`}>
                              {statut}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500">
                            {item.fournisseur && <span>{item.fournisseur} · </span>}
                            {item.quantite} × {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(item.prix_unitaire || 0)}
                            {item.date_commande && <span className="italic"> · Commandé le {fmtDateFR(item.date_commande)}</span>}
                            {item.date_livraison_reelle && <span className="italic"> · Livré le {fmtDateFR(item.date_livraison_reelle)}</span>}
                            {item.notes && <span className="italic"> · {item.notes}</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            value={statut}
                            onChange={async (e) => {
                              try {
                                await api.patch(`/chantiers/${id}/materiel/${item.item_id}/statut`, { statut: e.target.value });
                                toast.success('Statut mis à jour');
                                fetchMateriel();
                              } catch (err) {
                                toast.error('Erreur mise à jour');
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                            data-testid={`materiel-statut-${item.item_id}`}
                          >
                            {MAT_STATUS_ORDER.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                          <span className="text-sm font-semibold text-slate-900">
                            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(total)}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteMateriel(item.item_id)}
                            data-testid={`delete-materiel-${item.item_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Liste dépenses réelles */}
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-slate-700">
                  Factures d&apos;achat liées ({depenses.items?.length || 0})
                  {depenses.total_ttc > 0 && (
                    <span className="text-slate-500 font-normal"> — {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(depenses.total_ttc)} TTC</span>
                  )}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openLinkFAModal}
                  data-testid="link-facture-achat-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Lier une facture d&apos;achat
                </Button>
              </div>
              {(depenses.items || []).length === 0 ? (
                <p className="text-sm text-slate-500 py-3 italic">Aucune facture d&apos;achat liée à ce chantier.</p>
              ) : (
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                  {depenses.items.slice(0, 10).map((fa) => (
                    <div key={fa.facture_achat_id || fa.id} className="flex items-center justify-between p-3 hover:bg-slate-50">
                      <div
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => navigate(`/factures-achat/${fa.facture_achat_id}`)}
                      >
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {fa.fournisseur?.nom || 'Fournisseur inconnu'} — {fa.numero_facture || fa.numero || 'N/A'}
                        </p>
                        <p className="text-xs text-slate-500">
                          {fa.date_facture || '—'} · Statut : {fa.statut || 'À vérifier'}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-slate-900">
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(fa.montants?.total_ttc || 0)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleUnlinkFA(fa.facture_achat_id)}
                          title="Délier"
                          data-testid={`unlink-fa-${fa.facture_achat_id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {chantier.description_travaux && (
            <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200">
              <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-3 md:mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Description des Travaux
              </h2>
              <p className="text-slate-700 whitespace-pre-wrap">{chantier.description_travaux}</p>
            </div>
          )}

          {/* V3.1m-sprint3 — Pilotage chantier (Timeline, Avancement, Incidents, Réserves) */}
          <ChantierBusinessPanels chantierId={id} />

          {/* Section Rapports journaliers */}
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200" data-testid="rapports-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base md:text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Rapports journaliers ({rapports.length})
              </h2>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowRapportForm(!showRapportForm)}
                data-testid="toggle-add-rapport"
              >
                <Plus className="w-4 h-4 mr-1" />
                {showRapportForm ? 'Annuler' : 'Nouveau rapport'}
              </Button>
            </div>

            {showRapportForm && (
              <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Date</Label>
                    <Input
                      type="date"
                      value={newRapport.date}
                      onChange={(e) => setNewRapport({ ...newRapport, date: e.target.value })}
                      data-testid="rapport-date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Auteur du rapport *</Label>
                    <Input
                      value={newRapport.auteur}
                      onChange={(e) => setNewRapport({ ...newRapport, auteur: e.target.value })}
                      placeholder="Ex : Jean Martin (chef de chantier)"
                      data-testid="rapport-auteur"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Météo</Label>
                    <Input
                      value={newRapport.meteo}
                      onChange={(e) => setNewRapport({ ...newRapport, meteo: e.target.value })}
                      placeholder="Ex : Ensoleillé, 15°C"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Contenu de la journée *</Label>
                  <textarea
                    className="w-full min-h-[100px] px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newRapport.contenu}
                    onChange={(e) => setNewRapport({ ...newRapport, contenu: e.target.value })}
                    placeholder="Ce qui a été fait aujourd'hui : tâches réalisées, avancement, matériel utilisé…"
                    data-testid="rapport-contenu"
                  />
                </div>
                <div>
                  <Label className="text-xs">Incidents / points d'attention</Label>
                  <Input
                    value={newRapport.incidents}
                    onChange={(e) => setNewRapport({ ...newRapport, incidents: e.target.value })}
                    placeholder="Optionnel"
                  />
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={handleAddRapport} data-testid="rapport-save">Enregistrer</Button>
                </div>
              </div>
            )}

            {rapports.length === 0 ? (
              <p className="text-sm text-slate-500 italic text-center py-4">
                Aucun rapport journalier. Ajoutez-en un chaque jour pour tracer l'avancement.
              </p>
            ) : (
              <div className="space-y-3">
                {rapports.map((rap) => (
                  <div key={rap.rapport_id} className="p-3 border border-slate-200 rounded-lg hover:bg-slate-50" data-testid={`rapport-item-${rap.rapport_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-sm font-semibold text-slate-900">
                            {rap.date ? format(new Date(rap.date), 'dd MMM yyyy', { locale: fr }) : rap.date}
                          </span>
                          <Badge variant="secondary" className="text-[10px]">{rap.auteur}</Badge>
                          {rap.meteo && <span className="text-[11px] text-slate-500">{rap.meteo}</span>}
                        </div>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{rap.contenu}</p>
                        {rap.incidents && (
                          <p className="text-xs text-amber-700 mt-1.5 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                            ⚠ {rap.incidents}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                        onClick={() => handleDeleteRapport(rap.rapport_id)}
                        data-testid={`delete-rapport-${rap.rapport_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section Artisans Affectés */}
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200">
            <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-3 md:mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Artisans Affectés ({artisans.length})
            </h2>
            {artisans.length > 0 ? (
              <div className="space-y-3">
                {artisans.map((artisan) => (
                  <div
                    key={artisan.affectation_id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                  >
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                        <UserCircle className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-900 truncate">{artisan.nom}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
                          <Badge variant="secondary">{artisan.specialite}</Badge>
                          {artisan.telephone && (
                            <>
                              <span>•</span>
                              <span>{artisan.telephone}</span>
                            </>
                          )}
                        </div>
                        {artisan.mission && (
                          <div className="text-xs text-slate-600 mt-1 flex items-center gap-1.5">
                            <span className="text-slate-400">Mission :</span>
                            <input
                              type="text"
                              defaultValue={artisan.mission === 'À définir' ? '' : artisan.mission}
                              placeholder="Ex : Pose du parquet, Peinture chambre 1…"
                              onBlur={async (e) => {
                                const val = e.target.value.trim();
                                if (val && val !== artisan.mission) {
                                  try {
                                    await api.patch(`/affectations/${artisan.affectation_id}`, { mission: val });
                                    toast.success('Mission mise à jour');
                                    fetchArtisans();
                                  } catch (err) {
                                    toast.error('Erreur');
                                  }
                                }
                              }}
                              className="flex-1 min-w-0 px-1.5 py-0.5 text-xs border border-transparent hover:border-slate-200 focus:border-blue-400 rounded bg-transparent focus:bg-white focus:outline-none italic"
                              data-testid={`affectation-mission-${artisan.affectation_id}`}
                            />
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <label className="text-[11px] text-slate-500">Prestation :</label>
                          <select
                            value={artisan.type_prestation || "Main d'œuvre uniquement"}
                            onChange={async (e) => {
                              try {
                                await api.patch(`/affectations/${artisan.affectation_id}`, { type_prestation: e.target.value });
                                toast.success('Type de prestation mis à jour');
                                fetchArtisans();
                              } catch (err) {
                                toast.error('Erreur');
                              }
                            }}
                            className="text-xs border border-slate-200 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                            data-testid={`affectation-type-${artisan.affectation_id}`}
                          >
                            <option value="Main d'œuvre uniquement">Main d&apos;œuvre uniquement</option>
                            <option value="MO + fournitures">MO + fournitures</option>
                            <option value="Fournitures uniquement">Fournitures uniquement</option>
                          </select>
                          {artisan.montant_prevu != null && (
                            <span className="text-[11px] text-slate-600">
                              Prévu : <span className="font-semibold">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(artisan.montant_prevu)}</span>
                            </span>
                          )}
                          {artisan.acompte_verse > 0 && (
                            <span className="text-[11px] text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">
                              Acompte versé : {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(artisan.acompte_verse)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <Badge variant={artisan.statut === 'En cours' ? 'default' : 'secondary'}>
                        {artisan.statut}
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs text-blue-700 hover:bg-blue-50"
                        onClick={() => setMissionDialogArtisan(artisan)}
                        data-testid={`mission-generate-${artisan.sous_traitant_id}`}
                        title="Générer un bon de mission"
                      >
                        <FileSignature className="w-3.5 h-3.5 mr-1" /> Bon de mission
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleRemoveArtisan(artisan.affectation_id)}
                        data-testid={`remove-artisan-${artisan.sous_traitant_id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">Aucun artisan affecté à ce chantier</p>
            )}

            {/* Liste des bons de mission existants */}
            <MissionsList chantierId={id} refreshKey={missionsRefreshKey} />
          </div>

          {/* Modal génération de bon de mission */}
          <MissionDialog
            open={!!missionDialogArtisan}
            onOpenChange={(v) => { if (!v) setMissionDialogArtisan(null); }}
            chantier={chantier}
            artisan={missionDialogArtisan}
            onCreated={() => setMissionsRefreshKey(k => k + 1)}
          />

          {/* Section Photos */}
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200">
            <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-3 md:mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Photos du Chantier
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Photos Avant */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-slate-700 flex items-center gap-2">
                    <Camera className="w-4 h-4" /> Photos Avant
                  </h3>
                  <input
                    type="file"
                    ref={photoInputRefs.avant}
                    onChange={(e) => handlePhotoUpload(e, 'avant')}
                    className="hidden"
                    accept="image/*"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => photoInputRefs.avant.current?.click()}
                    disabled={uploadingPhoto === 'avant'}
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    {uploadingPhoto === 'avant' ? 'Upload...' : 'Ajouter'}
                  </Button>
                </div>
                <div className="space-y-2">
                  {chantier.photos_avant_urls && chantier.photos_avant_urls.length > 0 ? (
                    chantier.photos_avant_urls.map((url, idx) => (
                      <div key={idx} className="relative group">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt={`Avant ${idx + 1}`} className="w-full rounded-lg border" />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDeletePhoto('avant', url)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                      <Camera className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">Aucune photo</p>
                    </div>
                  )}
                </div>
              </div>
              {/* Photos Pendant */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-slate-700 flex items-center gap-2">
                    <Camera className="w-4 h-4" /> Photos Pendant
                  </h3>
                  <input
                    type="file"
                    ref={photoInputRefs.pendant}
                    onChange={(e) => handlePhotoUpload(e, 'pendant')}
                    className="hidden"
                    accept="image/*"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => photoInputRefs.pendant.current?.click()}
                    disabled={uploadingPhoto === 'pendant'}
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    {uploadingPhoto === 'pendant' ? 'Upload...' : 'Ajouter'}
                  </Button>
                </div>
                <div className="space-y-2">
                  {chantier.photos_pendant_urls && chantier.photos_pendant_urls.length > 0 ? (
                    chantier.photos_pendant_urls.map((url, idx) => (
                      <div key={idx} className="relative group">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt={`Pendant ${idx + 1}`} className="w-full rounded-lg border" />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDeletePhoto('pendant', url)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                      <Camera className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">Aucune photo</p>
                    </div>
                  )}
                </div>
              </div>
              {/* Photos Après */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-slate-700 flex items-center gap-2">
                    <Camera className="w-4 h-4" /> Photos Après
                  </h3>
                  <input
                    type="file"
                    ref={photoInputRefs.apres}
                    onChange={(e) => handlePhotoUpload(e, 'apres')}
                    className="hidden"
                    accept="image/*"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => photoInputRefs.apres.current?.click()}
                    disabled={uploadingPhoto === 'apres'}
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    {uploadingPhoto === 'apres' ? 'Upload...' : 'Ajouter'}
                  </Button>
                </div>
                <div className="space-y-2">
                  {chantier.photos_apres_urls && chantier.photos_apres_urls.length > 0 ? (
                    chantier.photos_apres_urls.map((url, idx) => (
                      <div key={idx} className="relative group">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt={`Après ${idx + 1}`} className="w-full rounded-lg border" />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDeletePhoto('apres', url)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                      <Camera className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">Aucune photo</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">{contact && (
            <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-3">Contact</h3>
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-900">{contact.nom_prenom}</p>
                <p className="text-sm text-slate-600">{contact.telephone}</p>
                {contact.email && <p className="text-sm text-slate-600">{contact.email}</p>}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-3"
                  onClick={() => navigate(`/contacts/${contact.contact_id}`)}
                >
                  Voir le contact
                </Button>
              </div>
            </div>
          )}

          {chantier.devis_id && (
            <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-3">Devis Associé</h3>
              <p className="text-sm font-mono text-slate-600 mb-3">{chantier.devis_id}</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => navigate(`/devis/${chantier.devis_id}`)}
              >
                Voir le devis
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Modifier le Chantier</DialogTitle>
          </DialogHeader>
          <ChantierForm
            chantier={chantier}
            onSuccess={handleChantierUpdated}
            onCancel={() => setShowEdit(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Acompte Dialog */}
      <Dialog open={showAcompte} onOpenChange={setShowAcompte}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un Acompte</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {chantier?.montant_total_devis && (
              <p className="text-sm text-slate-500 bg-slate-50 p-2 rounded">
                Montant total du devis : {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(chantier.montant_total_devis)}
              </p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="montant">Montant (€) *</Label>
                <Input
                  id="montant"
                  type="number"
                  placeholder="1500"
                  value={newAcompte.montant}
                  onChange={(e) => handleMontantChange(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="pourcentage">Pourcentage (%)</Label>
                <Input
                  id="pourcentage"
                  type="number"
                  placeholder="30"
                  value={newAcompte.pourcentage}
                  onChange={(e) => handlePourcentageChange(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="statut">Statut</Label>
              <Select
                value={newAcompte.statut}
                onValueChange={(value) => setNewAcompte({ ...newAcompte, statut: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Statut de l'acompte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Attendu">Attendu</SelectItem>
                  <SelectItem value="Encaissé">Encaissé</SelectItem>
                  <SelectItem value="Annulé">Annulé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                placeholder="Notes sur l'acompte..."
                value={newAcompte.notes}
                onChange={(e) => setNewAcompte({ ...newAcompte, notes: e.target.value })}
              />
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setShowAcompte(false)}>
                Annuler
              </Button>
              <Button onClick={handleAddAcompte}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Panneau Tâches liées + bouton Discuter ce chantier */}
      <EntityTasksPanel
        entityType="chantier"
        entityId={id}
        entityLabel={chantier?.project_title || chantier?.nom || chantier?.chantier_id}
      />

      {/* Modal — Lier une facture d'achat existante */}
      <Dialog open={showLinkFAModal} onOpenChange={setShowLinkFAModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lier une facture d&apos;achat au chantier</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {loadingFA ? (
              <p className="text-sm text-slate-500 py-8 text-center">Chargement…</p>
            ) : availableFA.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center italic">
                Aucune facture d&apos;achat disponible (toutes sont déjà liées à un chantier).
              </p>
            ) : (
              availableFA.map((fa) => (
                <button
                  key={fa.facture_achat_id}
                  type="button"
                  onClick={() => handleLinkFA(fa.facture_achat_id)}
                  className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors text-left"
                  data-testid={`link-fa-option-${fa.facture_achat_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {fa.fournisseur?.nom || 'Fournisseur inconnu'} — {fa.numero_facture || 'N/A'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {fa.date_facture || '—'} · Statut : {fa.statut || 'À vérifier'}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-slate-900 ml-3">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(fa.montants?.total_ttc || 0)}
                  </span>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// PILOTAGE — Vue synthétique en 4 sous-blocs (Planning · Matériaux · Sous-traitants · Facturation)
// ============================================================================
const MAT_STATUS_ORDER = ['À chiffrer', 'Devis reçu', 'Commandé', 'Livré', 'Posé'];
const MAT_STATUS_COLORS = {
  'À chiffrer': 'bg-slate-100 text-slate-700 border-slate-200',
  'Devis reçu': 'bg-blue-100 text-blue-700 border-blue-200',
  'Commandé': 'bg-amber-100 text-amber-700 border-amber-200',
  'Livré': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Posé': 'bg-purple-100 text-purple-700 border-purple-200',
};

function fmtDateFR(iso) {
  if (!iso) return null;
  try {
    return format(new Date(iso), 'dd MMM yyyy', { locale: fr });
  } catch (e) { return iso; }
}

function fmtEUR(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
}

function PilotageBloc({ chantier, materiel, depenses, artisans, factures, onGoDevis, onUpdateMaterielStatut }) {
  const dateDebut = chantier?.date_debut;
  const dateFin = chantier?.date_fin_estimee;
  const today = new Date();
  let daysUntilStart = null;
  let planningState = 'not-scheduled';
  if (dateDebut) {
    const d1 = new Date(dateDebut);
    const diff = Math.ceil((d1 - today) / (1000 * 60 * 60 * 24));
    daysUntilStart = diff;
    if (diff > 0) planningState = 'scheduled';
    else if (dateFin && new Date(dateFin) >= today) planningState = 'in-progress';
    else planningState = 'past';
  }

  const matCount = materiel.length;
  const matByStatut = MAT_STATUS_ORDER.reduce((acc, s) => {
    acc[s] = materiel.filter((m) => (m.statut || 'À chiffrer') === s).length;
    return acc;
  }, {});
  const matReady = (matByStatut['Livré'] || 0) + (matByStatut['Posé'] || 0);
  const matReadyPct = matCount > 0 ? Math.round((matReady / matCount) * 100) : 0;
  const totalMatEstime = materiel.reduce((s, it) => s + (parseFloat(it.prix_unitaire) || 0) * (parseFloat(it.quantite) || 1), 0);
  const totalMatReel = depenses?.total_ttc || 0;

  const artisansCount = artisans.length;
  const stByType = artisans.reduce((acc, a) => {
    const t = a.type_prestation || "Main d'œuvre uniquement";
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  const hasDevis = !!chantier?.devis_id;
  const facturesCount = factures.length;
  const facturePayee = factures.filter((f) => f.statut === 'Payée').length;
  const factureEmise = factures.filter((f) => ['Émise', 'Envoyée Chorus'].includes(f.statut)).length;
  const totalFactureTTC = factures.reduce((s, f) => s + (f.totaux?.total_ttc || 0), 0);
  const budgetTotal = chantier?.montant_total_devis || chantier?.budget_prevu || 0;

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50/30 rounded-2xl border border-slate-200 p-3 md:p-5" data-testid="pilotage-section">
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-base md:text-xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Pilotage du chantier
          </h2>
          <p className="text-[11px] md:text-xs text-slate-500 truncate">Vue synthétique : où en est-on ?</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3">
        <div className="bg-white rounded-xl border border-blue-200 p-3 md:p-4" data-testid="pilotage-planning">
          <div className="flex items-center gap-2 mb-3">
            <CalendarIcon className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-semibold text-slate-900">Planning</h3>
          </div>
          {planningState === 'not-scheduled' ? (
            <div className="text-center py-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 mx-auto mb-1" />
              <p className="text-xs font-semibold text-amber-700">Non planifié</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Aucune date de début définie</p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="text-slate-500 min-w-[54px]">Début :</span>
                  <span className="font-semibold text-slate-900">{fmtDateFR(dateDebut) || '—'}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="text-slate-500 min-w-[54px]">Fin :</span>
                  <span className="font-semibold text-slate-900">{fmtDateFR(dateFin) || '—'}</span>
                </div>
              </div>
              <div className={`mt-3 text-center py-1.5 rounded-md text-xs font-semibold ${
                planningState === 'in-progress' ? 'bg-green-50 text-green-700' :
                planningState === 'past' ? 'bg-slate-50 text-slate-600' :
                daysUntilStart <= 3 ? 'bg-red-50 text-red-700' :
                daysUntilStart <= 7 ? 'bg-amber-50 text-amber-700' :
                'bg-blue-50 text-blue-700'
              }`}>
                {planningState === 'in-progress' ? 'En cours' :
                 planningState === 'past' ? 'Passé' :
                 daysUntilStart === 0 ? "Démarre aujourd'hui" :
                 daysUntilStart === 1 ? 'Démarre demain' :
                 `Dans ${daysUntilStart} jours`}
              </div>
            </>
          )}
        </div>

        <div className="bg-white rounded-xl border border-amber-200 p-3 md:p-4" data-testid="pilotage-materiaux">
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-semibold text-slate-900">Matériaux</h3>
            <span className="ml-auto text-[10px] font-semibold text-slate-500">{matCount} ligne{matCount > 1 ? 's' : ''}</span>
          </div>
          {matCount === 0 ? (
            <div className="text-center py-3">
              <p className="text-xs text-slate-400 italic">Aucun matériel prévu.</p>
            </div>
          ) : (
            <>
              <div className="mb-2">
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className="text-slate-500">Prêt (Livré+Posé)</span>
                  <span className="font-semibold text-slate-900">{matReadyPct}%</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${matReadyPct === 100 ? 'bg-emerald-500' : 'bg-amber-400'}`}
                    style={{ width: `${matReadyPct}%` }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-5 gap-1 mt-2">
                {MAT_STATUS_ORDER.map((s) => (
                  <div key={s} className="text-center" title={s}>
                    <div className={`h-5 rounded flex items-center justify-center text-[10px] font-semibold border ${MAT_STATUS_COLORS[s]}`}>
                      {matByStatut[s] || 0}
                    </div>
                    <p className="text-[9px] text-slate-500 mt-0.5 truncate">{s.split(' ')[0]}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] space-y-0.5">
                <div className="flex justify-between">
                  <span className="text-slate-500">Estimé :</span>
                  <span className="font-semibold">{fmtEUR(totalMatEstime)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Réel :</span>
                  <span className={`font-semibold ${totalMatReel > totalMatEstime && totalMatEstime > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                    {fmtEUR(totalMatReel)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="bg-white rounded-xl border border-indigo-200 p-3 md:p-4" data-testid="pilotage-sous-traitants">
          <div className="flex items-center gap-2 mb-3">
            <UsersIcon className="w-4 h-4 text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-900">Sous-traitants</h3>
            <span className="ml-auto text-[10px] font-semibold text-slate-500">{artisansCount}</span>
          </div>
          {artisansCount === 0 ? (
            <div className="text-center py-3">
              <p className="text-xs text-slate-400 italic">Aucun artisan affecté.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {Object.entries(stByType).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between text-xs">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                    type === "MO + fournitures" ? 'bg-purple-50 text-purple-700' :
                    type === "Fournitures uniquement" ? 'bg-emerald-50 text-emerald-700' :
                    'bg-blue-50 text-blue-700'
                  }`}>
                    {type}
                  </span>
                  <span className="font-semibold text-slate-900">{count}</span>
                </div>
              ))}
              <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                {artisans.slice(0, 2).map((a) => (
                  <div key={a.affectation_id} className="text-[11px] text-slate-600 truncate">
                    • {a.mission || a.sous_traitant?.nom || 'Artisan'}
                  </div>
                ))}
                {artisans.length > 2 && (
                  <p className="text-[10px] text-slate-400">+ {artisans.length - 2} autre(s)</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-green-200 p-3 md:p-4" data-testid="pilotage-facturation">
          <div className="flex items-center gap-2 mb-3">
            <Receipt className="w-4 h-4 text-green-600" />
            <h3 className="text-sm font-semibold text-slate-900">Facturation</h3>
          </div>
          <div className="flex items-center gap-1.5 text-xs mb-1.5">
            {hasDevis ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Circle className="w-3.5 h-3.5 text-slate-300" />}
            <span className={hasDevis ? 'text-slate-900' : 'text-slate-400'}>
              Devis lié {hasDevis && <button type="button" onClick={onGoDevis} className="text-blue-600 hover:underline ml-1">({chantier.devis_id})</button>}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-xs mb-1.5">
            {factureEmise > 0 || facturePayee > 0 ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Circle className="w-3.5 h-3.5 text-slate-300" />}
            <span className={facturesCount > 0 ? 'text-slate-900' : 'text-slate-400'}>
              Facture émise ({facturesCount})
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-xs mb-1.5">
            {facturePayee > 0 ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Circle className="w-3.5 h-3.5 text-slate-300" />}
            <span className={facturePayee > 0 ? 'text-slate-900' : 'text-slate-400'}>
              Facture payée ({facturePayee})
            </span>
          </div>
          <div className="mt-2.5 pt-2.5 border-t border-slate-100 text-[11px] space-y-0.5">
            <div className="flex justify-between">
              <span className="text-slate-500">Devis :</span>
              <span className="font-semibold">{fmtEUR(budgetTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Facturé :</span>
              <span className={`font-semibold ${totalFactureTTC >= budgetTotal && budgetTotal > 0 ? 'text-emerald-600' : 'text-slate-900'}`}>
                {fmtEUR(totalFactureTTC)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Sélecteur inline du statut matériel (utilisé dans la section Matériel & Marges)
export function MaterielStatutSelector({ currentStatut, onChange }) {
  return (
    <select
      value={currentStatut || 'À chiffrer'}
      onChange={(e) => { e.stopPropagation(); onChange(e.target.value); }}
      onClick={(e) => e.stopPropagation()}
      className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
    >
      {MAT_STATUS_ORDER.map((s) => (
        <option key={s} value={s}>{s}</option>
      ))}
    </select>
  );
}

